/**
 * OffenceService - Handles offence and category management
 */

import { OffenceRepository } from '../repositories/OffenceRepository';
import { validateOffenceData } from '../utils/validationHelpers';

export class OffenceService {
  constructor(offenceRepo = null) {
    this.offenceRepo = offenceRepo || new OffenceRepository();
  }

  /**
   * Create a new offence
   * @param {Object} offenceData - Offence data
   * @returns {Promise<Object>} Created offence
   */
  async createOffence(offenceData) {
    try {
      // Validate offence data
      const validation = validateOffenceData(offenceData);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      return await this.offenceRepo.createOffence(offenceData);
    } catch (error) {
      console.error('Error creating offence:', error);
      throw error;
    }
  }

  /**
   * Get all offences
   * @param {string} schoolId - School ID (required)
   * @param {boolean} activeOnly - Only return active offences
   * @returns {Promise<Array>} Array of offences
   */
  async getOffences(schoolId, activeOnly = true) {
    // Guard: require schoolId to prevent permission errors
    if (!schoolId) {
      console.warn('OffenceService.getOffences: schoolId required but not provided');
      return [];
    }

    try {
      if (activeOnly) {
        return await this.offenceRepo.getActiveOffences(schoolId);
      }
      return await this.offenceRepo.getAllOffences(schoolId);
    } catch (error) {
      console.error('Error fetching offences:', error);
      return [];
    }
  }

  /**
   * Subscribe to offence changes
   * @param {string} schoolId - School ID (required)
   * @param {Function} callback - Callback for updates
   * @param {boolean} activeOnly - Only subscribe to active offences
   * @returns {Function} Unsubscribe function
   */
  subscribeOffences(schoolId, callback, activeOnly = true) {
    // Guard: require schoolId to prevent permission errors
    if (!schoolId) {
      console.warn('OffenceService.subscribeOffences: schoolId required but not provided');
      callback([]);
      return () => {}; // Return no-op unsubscribe
    }

    // No schoolId constraint needed - hierarchical path (schools/{schoolId}/offences) handles scoping
    const constraints = [];

    if (activeOnly) {
      constraints.push({ field: 'active', operator: '==', value: true });
    }

    // Pass schoolId for hierarchical path routing, not as a query filter
    return this.offenceRepo.subscribe(callback, constraints, schoolId);
  }

  /**
   * Update an offence
   * @param {string} offenceId - Offence ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated offence
   */
  async updateOffence(offenceId, updates) {
    try {
      // Validate updates if changing critical fields
      if (updates.name || updates.category || updates.level) {
        const validation = validateOffenceData({
          name: updates.name || 'existing',
          category: updates.category || 'existing',
          level: updates.level || 1
        });

        if (!validation.isValid && updates.name && updates.category && updates.level) {
          throw new Error(validation.error);
        }
      }

      return await this.offenceRepo.updateOffence(offenceId, updates);
    } catch (error) {
      console.error('Error updating offence:', error);
      throw error;
    }
  }

  /**
   * Delete an offence
   * @param {string} offenceId - Offence ID
   * @returns {Promise<void>}
   */
  async deleteOffence(offenceId) {
    try {
      // Prefer deactivation over deletion
      return await this.offenceRepo.deactivateOffence(offenceId);
    } catch (error) {
      console.error('Error deleting offence:', error);
      throw error;
    }
  }

  /**
   * Get offence by ID
   * @param {string} offenceId - Offence ID
   * @returns {Promise<Object|null>} Offence or null
   */
  async getOffenceById(offenceId) {
    try {
      return await this.offenceRepo.getById(offenceId);
    } catch (error) {
      console.error('Error fetching offence by ID:', error);
      return null;
    }
  }

  /**
   * Get offence by name
   * @param {string} name - Offence name
   * @returns {Promise<Object|null>} Offence or null
   */
  async getOffenceByName(name, schoolId = null) {
    try {
      return await this.offenceRepo.getByName(name, schoolId);
    } catch (error) {
      console.error('Error fetching offence by name:', error);
      return null;
    }
  }

  /**
   * Get offences by category
   * @param {string} categoryId - Category ID
   * @returns {Promise<Array>} Array of offences
   */
  async getOffencesByCategory(categoryId) {
    try {
      return await this.offenceRepo.getByCategory(categoryId);
    } catch (error) {
      console.error('Error fetching offences by category:', error);
      return [];
    }
  }

  // Category Management

  /**
   * Create a new category
   * @param {Object} categoryData - Category data
   * @returns {Promise<Object>} Created category
   */
  async createDisciplineCategory(categoryData) {
    try {
      return await this.offenceRepo.createCategory(categoryData);
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  /**
   * Get all categories
   * @param {string} schoolId - School ID (required)
   * @returns {Promise<Array>} Array of categories
   */
  async getDisciplineCategories(schoolId) {
    if (!schoolId) {
      console.warn('getDisciplineCategories called without schoolId');
      return [];
    }

    try {
      return await this.offenceRepo.getAllCategories(schoolId);
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  }

  /**
   * Subscribe to category changes
   * @param {string} schoolId - School ID (required)
   * @param {Function} callback - Callback for updates
   * @returns {Function} Unsubscribe function
   */
  subscribeDisciplineCategories(schoolId, callback) {
    // Guard: require schoolId to prevent permission errors
    if (!schoolId) {
      console.warn('OffenceService.subscribeDisciplineCategories: schoolId required but not provided');
      callback([]);
      return () => {}; // Return no-op unsubscribe
    }

    return this.offenceRepo.categoriesRepo.subscribe(callback, [], schoolId);
  }

  /**
   * Update a category
   * @param {string} categoryId - Category ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated category
   */
  async updateDisciplineCategory(categoryId, updates) {
    try {
      return await this.offenceRepo.updateCategory(categoryId, updates);
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  /**
   * Delete a category
   * @param {string} categoryId - Category ID
   * @returns {Promise<void>}
   */
  async deleteDisciplineCategory(categoryId) {
    try {
      return await this.offenceRepo.deleteCategory(categoryId);
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  /**
   * Get category by ID
   * @param {string} categoryId - Category ID
   * @returns {Promise<Object|null>} Category or null
   */
  async getCategoryById(categoryId) {
    try {
      return await this.offenceRepo.getCategoryById(categoryId);
    } catch (error) {
      console.error('Error fetching category by ID:', error);
      return null;
    }
  }

  /**
   * Get category by name
   * @param {string} name - Category name
   * @returns {Promise<Object|null>} Category or null
   */
  async getCategoryByName(name) {
    try {
      return await this.offenceRepo.getCategoryByName(name);
    } catch (error) {
      console.error('Error fetching category by name:', error);
      return null;
    }
  }

  /**
   * Reorder categories
   * @param {Array} categoryIds - Array of category IDs in new order
   * @returns {Promise<Array>} Updated categories
   */
  async reorderCategories(categoryIds) {
    try {
      return await this.offenceRepo.reorderCategories(categoryIds);
    } catch (error) {
      console.error('Error reordering categories:', error);
      throw error;
    }
  }

  /**
   * Get offences grouped by category
   * @returns {Promise<Object>} Offences grouped by category
   */
  async getOffencesGroupedByCategory() {
    try {
      return await this.offenceRepo.getOffencesGroupedByCategory();
    } catch (error) {
      console.error('Error fetching grouped offences:', error);
      return {};
    }
  }

  /**
   * Search offences by name
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Matching offences
   */
  async searchOffences(searchTerm) {
    try {
      return await this.offenceRepo.searchOffences(searchTerm);
    } catch (error) {
      console.error('Error searching offences:', error);
      return [];
    }
  }

  /**
   * Get offence statistics
   * @returns {Promise<Object>} Statistics
   */
  async getOffenceStatistics() {
    try {
      return await this.offenceRepo.getOffenceStatistics();
    } catch (error) {
      console.error('Error fetching offence statistics:', error);
      return {
        totalOffences: 0,
        activeOffences: 0,
        totalCategories: 0,
        offencesByLevel: {}
      };
    }
  }

  /**
   * Import offences from array
   * @param {Array} offences - Array of offence data
   * @returns {Promise<Object>} Import results
   */
  async importOffences(offences, schoolId = null) {
    try {
      const results = {
        success: [],
        failed: [],
        duplicates: []
      };

      for (const offence of offences) {
        try {
          // Check for duplicates (pass schoolId for hierarchical collections)
          const existing = await this.getOffenceByName(offence.name, schoolId || offence.schoolId);
          if (existing) {
            results.duplicates.push(offence);
            continue;
          }

          const created = await this.createOffence(offence);
          results.success.push(created);
        } catch (error) {
          results.failed.push({ offence, error: error.message });
        }
      }

      return results;
    } catch (error) {
      console.error('Error importing offences:', error);
      throw error;
    }
  }
}